import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    computed,
    effect,
    inject,
    input,
    signal,
    untracked,
    viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

import { addDays, addHours, addMonths, addWeeks, addYears, endOfMonth, isFirstDayOfMonth, subMinutes } from 'date-fns';
import { catchError, combineLatest, finalize, map, of, startWith, switchMap } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

import { Button, ButtonIcon, ButtonLabel } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { InputNumber } from 'primeng/inputnumber';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';

import { ProductsApiService } from '@bcmApiServices/product.api-service';
import { ProductUnitApiService } from '@bcmApiServices/product-unit.api-service';
import { ProductTaxRateApiService } from '@bcmApiServices/product-tax-rate.api-service';
import { ProductCategoryApiService } from '@bcmApiServices/product-category.api-service';
import { PayableOptionId, ProductPayableOptionApiService } from '@bcmApiServices/product-payable-option.api-service';
import { PersonApiService } from '@bcmApiServices/person.api-service';
import { CompanyApiService } from '@bcmApiServices/company.api-service';
import { BcmFinancialRecordsApiService } from '@bcmApiServices/bcm-financial-records-api.service';
import { CostCentersFacade } from '@modules/bcm/@core/state-management/cost-centers/cost-centers.facade';
import { BcmService } from '@modules/bcm/bcm.service';
import { BcmTenantService } from '@modules/bcm/bcm-tenant.service';
import { BcmTenantPermission } from '@modules/bcm/bcm-tenant-permission';
import { TranslationModule } from '@core/translation/translation.module';
import { TolgeeService } from '@core/translation/tolgee.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { BcmCostCenter } from '@shared/models/bcm-cost-center';
import { sortCostCentersByName } from '@shared/functions/sort-cost-centers-by-name';
import { ICategory } from '@shared/models/category';
import { PayableOption } from '@shared/models/payable-option';
import { Product } from '@shared/models/product';
import { IUnit, UnitUniqueName } from '@shared/models/unit';
import { ProduktQuantityButton } from '@shared/models/product-quantity-button';
import { buildAreaQuantityButtons } from '@shared/functions/build-area-quantity-buttons';
import { ITaxRate } from '@shared/models/tax-rate';
import { PageRequest } from '@shared/models/pagination';
import { BcmFinancialRecord, FinancialRecordStatus } from '@shared/models/bcm-financial-record';
import { tryParseDate } from '@shared/functions/try-parse-date';
import { U2bConfirmDialogComponent } from '@u2bUi/dialog/u2b-confirm-dialog.component';
import { U2bDatePickerButtonBarComponent } from '@u2bUi/date-picker/u2b-date-picker-button-bar.component';
import { U2B_DATE_PICKER_CUSTOM_TIME_PT } from '@u2bUi/date-picker/u2b-date-picker.pt';
import { U2bDateMaskDirective } from '@u2bUi/directives/u2b-date-mask.directive';
import { U2bDatePickerApplyDirective } from '@u2bUi/directives/u2b-date-picker-apply.directive';
import { U2bIconButtonComponent } from '@u2bUi/icon-button/u2b-icon-button.component';
import { U2bTabSwitcherComponent, U2bTabSwitcherItem } from '@u2bUi/tab-switcher/u2b-tab-switcher.component';

import { Season } from '@shared/models/seasons';

import {
    BookingV2Receipt,
    BookingV2ReceiptStatusKind,
    BookingV2RelationOption,
    BookingV2Service,
    BookingV2Subscription,
    BookingV2SubscriptionConfig,
    BookingV2WinterStorageOption,
} from '../../../dtos/booking-v2.dtos';
import { BookingV2DynamicPriceBreakdownComponent } from '../../../components/dynamic-price-breakdown/booking-v2-dynamic-price-breakdown.component';
import {
    BookingV2ServiceInlineCommit,
    BookingV2ServiceInlineEntryComponent,
} from '../../../components/service-inline-entry/booking-v2-service-inline-entry.component';
import { BookingV2Facade } from '../../../services/booking-v2.facade';
import { BookingV2RelationPricingService } from '../../../services/booking-v2-relation-pricing.service';
import { formatMoney, formatQuantity, grossOf } from '../../../utils/booking-v2-pricing';
import { createBookingV2ServiceFromProduct } from '../../../utils/booking-v2-service-from-product.util';
import { editedServiceName } from '../../../utils/booking-v2-service-naming';
import { BOOKING_V2_ADVANCED_SERVICES_TAB_LABEL_KEY } from '../booking-v2-advanced-i18n';

type ServicesSubTab = 'services' | 'subscriptions' | 'receipts';

// Catalog row built from a backend `Product`. Carries real reference ids so a
// picked product becomes a fully-resolved `BookingV2Service` line.
interface CatalogProduct {
    id: number;
    name: string;
    category: string | null;
    unitId: number | null;
    unitLabelKey: string | null;
    price: number;
    taxRateId: number | null;
    vatValue: number;
    // Carries the rule flag + the entities its rules reference so an added line can
    // be priced dynamically (and feed the combined season gating) — the catalog list
    // endpoint eager-loads priceRuleMap, so getRequirements() resolves here.
    hasDynamicPrice: boolean;
    requiredEntities: readonly string[];
    tenantRelationCriteriaIds: readonly number[];
}

const RECEIPT_STATUS_LABEL_KEYS: Record<BookingV2ReceiptStatusKind, string> = {
    paid: 'bookingV2.advanced.services.receipts.status.paid',
    partial: 'bookingV2.advanced.services.receipts.status.partial',
    open: 'bookingV2.advanced.services.receipts.status.open',
    overdue: 'bookingV2.advanced.services.receipts.status.overdue',
    cancelled: 'bookingV2.advanced.services.receipts.status.cancelled',
};

// Map a raw backend ProductSubscription (persons/:id / companies/:id eager-load
// `subscriptions` as `any[]` with product + payableOption + dynamicPrice) into
// the read-only view model.
function toBookingSubscription(raw: any): BookingV2Subscription {
    const end = tryParseDate(raw?.toDate);
    const quantity = raw?.quantity ?? 1;
    const unitPrice = raw?.customPrice ?? raw?.dynamicPrice?.rulePrice ?? raw?.product?.price ?? 0;
    return {
        id: String(raw?.id ?? raw?.uuid ?? uuidv4()),
        description: raw?.customTitle || raw?.dynamicPrice?.ruleName || raw?.product?.name || '',
        quantity,
        unitLabelKey: raw?.product?.unit?.translationKey ?? null,
        unitPrice,
        total: unitPrice * quantity,
        startDate: tryParseDate(raw?.fromDate),
        intervalLabelKey: raw?.payableOption?.translationKey ?? null,
        isActive: !end || end.getTime() >= Date.now(),
    };
}

// `overdue` is derived (open + due date in the past), not a backend status.
function toReceiptStatusKind(status: FinancialRecordStatus, dueDate: Date | null): BookingV2ReceiptStatusKind {
    switch (status) {
        case FinancialRecordStatus.Payed:
        case FinancialRecordStatus.Done:
        case FinancialRecordStatus.BookedOut:
            return 'paid';
        case FinancialRecordStatus.PaidPartially:
            return 'partial';
        case FinancialRecordStatus.Canceled:
            return 'cancelled';
        default:
            return dueDate && dueDate.getTime() < Date.now() ? 'overdue' : 'open';
    }
}

function toBookingReceipt(record: BcmFinancialRecord): BookingV2Receipt {
    return {
        id: record.number,
        typeLabelKey: record.type,
        number: record.number,
        date: record.date ?? null,
        dueDate: record.dueDate ?? null,
        status: toReceiptStatusKind(record.status, record.dueDate ?? null),
        grossAmount: record.total ?? 0,
        paymentMethodKey: record.paymentTypeKey ?? null,
    };
}

function toCatalogProduct(p: Product): CatalogProduct {
    return {
        id: p.id,
        name: p.name,
        category: p.category?.name ?? null,
        unitId: p.unit?.id ?? null,
        unitLabelKey: p.unit?.translationKey ?? null,
        price: p.price,
        taxRateId: p.taxRate?.id ?? null,
        vatValue: p.taxRate?.value ?? 0,
        hasDynamicPrice: !!p.hasDynamicPrice,
        requiredEntities: p.priceRuleMap?.getRequirements() ?? [],
        tenantRelationCriteriaIds: p.priceRuleMap?.getTenantRelationCriteriaIds() ?? [],
    };
}

@Component({
    selector: 'booking-v2-services-tab',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        Button,
        ButtonIcon,
        ButtonLabel,
        Checkbox,
        DatePicker,
        InputNumber,
        InputText,
        ProgressSpinner,
        Select,
        Tooltip,
        TranslationModule,
        BookingV2DynamicPriceBreakdownComponent,
        BookingV2ServiceInlineEntryComponent,
        U2bDateMaskDirective,
        U2bDatePickerApplyDirective,
        U2bDatePickerButtonBarComponent,
        U2bIconButtonComponent,
        U2bTabSwitcherComponent,
    ],
    templateUrl: './services-tab.component.html',
    host: { class: 'block' },
    // Recolor the disabled "open end" datepicker to the primary accent — same as
    // the date-range step.
    styles: [`
        .u2b-open-end-field {
            --p-inputtext-border-color: var(--p-primary-color);
            --p-inputtext-placeholder-color: var(--p-primary-color);
            --p-inputtext-disabled-color: var(--p-primary-color);
            --p-inputtext-disabled-background: color-mix(in srgb, var(--p-primary-color) 8%, transparent);
            --p-datepicker-dropdown-border-color: var(--p-primary-color);
            --p-datepicker-dropdown-color: var(--p-primary-color);
        }

        /* Compact icon buttons for the per-row action column only — does not
           affect u2b-icon-button elsewhere. */
        :host ::ng-deep .u2b-row-actions .p-button {
            width: 2rem;
            height: 2rem;
            min-width: 2rem;
            padding: 0;
        }
        :host ::ng-deep .u2b-row-actions .p-button .material-icons {
            font-size: 1.25rem;
        }

        .booking-v2-service-inline-field {
            border: none;
            border-bottom: 1px solid var(--p-border-color, rgba(0, 0, 0, 0.2));
            outline: none;
            background: transparent;
            font-size: 0.875rem;
            font-family: inherit;
            color: var(--p-text-color);
            padding: 0.125rem 0.25rem;
            width: 100%;
            box-sizing: border-box;
        }

        .booking-v2-service-inline-field:focus {
            border-bottom: 2px solid var(--p-primary-color);
        }

        .booking-v2-services-grid-header > div {
            overflow: visible;
            white-space: nowrap;
        }

        .booking-v2-service-inline-field.inline-field--unit {
            min-width: 5.5rem;
        }

        .inline-price-trigger {
            display: block;
            width: 100%;
            margin: 0;
            padding: 0.125rem 0.25rem;
            border: none;
            border-bottom: 1px solid var(--p-border-color, rgba(0, 0, 0, 0.2));
            background: transparent;
            font-size: 0.875rem;
            font-family: inherit;
            color: var(--p-text-color);
            text-align: right;
            line-height: 1.4;
            cursor: text;
            box-sizing: border-box;
            white-space: nowrap;
        }

        .inline-price-trigger:focus {
            border-bottom: 2px solid var(--p-primary-color);
            outline: none;
        }
    `],
})
export class ServicesTabComponent {

    private readonly hostRef = inject(ElementRef<HTMLElement>);
    private readonly inlineEntry = viewChild(BookingV2ServiceInlineEntryComponent);

    /** Hides PrimeNG's read-only clock; the button bar renders a typable one. */
    readonly customTimePt = U2B_DATE_PICKER_CUSTOM_TIME_PT;

    private readonly facade = inject(BookingV2Facade);
    private readonly relationPricing = inject(BookingV2RelationPricingService);
    private readonly productsApi = inject(ProductsApiService);
    private readonly categoryApi = inject(ProductCategoryApiService);
    private readonly unitApi = inject(ProductUnitApiService);
    private readonly taxRateApi = inject(ProductTaxRateApiService);
    private readonly notifications = inject(AppNotificationService);
    private readonly payableOptionApi = inject(ProductPayableOptionApiService);
    private readonly personApi = inject(PersonApiService);
    private readonly companyApi = inject(CompanyApiService);
    private readonly financialRecordsApi = inject(BcmFinancialRecordsApiService);
    private readonly costCentersFacade = inject(CostCentersFacade);
    private readonly tenantService = inject(BcmTenantService);
    private readonly overlay = inject(Overlay);

    // PrimeNG inputnumber locale for German decimals (defaults to de-DE).
    readonly currentLocale = BcmService.currentLocale;

    // Cost centers are a per-tenant feature (COST_CENTERS permission); only show
    // the field when the tenant has it (matches the legacy position dialog).
    readonly showCostCenter = computed(() =>
        this.tenantService.checkPermission(BcmTenantPermission.COST_CENTERS),
    );

    readonly costCenters = toSignal(
        this.costCentersFacade.loadPage(new HttpParams().set('size', '1000')).pipe(
            map(page => sortCostCentersByName(page.results)),
            catchError(() => of<BcmCostCenter[]>([])),
        ),
        { initialValue: [] as BcmCostCenter[] },
    );

    // Read-only mode (e.g. the summary step): hides the catalog/add/edit/remove
    // affordances; the three sub-tabs render as plain read-only tables.
    readonly readOnly = input<boolean>(false);

    readonly services = this.facade.services;
    readonly receiptStatusLabelKeys = RECEIPT_STATUS_LABEL_KEYS;

    private readonly draftSync = effect(() =>
        // Mark the advanced step dirty while an entry editor or the new-product
        // form is open & unsaved, so navigating away prompts to discard.
        this.facade.advancedDraftDirty.set(
            !this.readOnly() && (
                this.editingId() !== null
                || this.showNewProductForm()
                || (this.inlineEntry()?.hasDraft() ?? false)
            ),
        ),
    );
    readonly grossOf = grossOf;

    // ── Real backend reference data ──
    readonly loadingCatalog = signal<boolean>(true);

    // Bumped after a new ad-hoc product is saved to the catalog so the search list
    // re-fetches and the just-created product becomes findable in the same session.
    private readonly catalogReload = signal(0);

    readonly products = toSignal(
        toObservable(this.catalogReload).pipe(
            switchMap(() => this.productsApi.getAll().pipe(
                catchError(() => of<Product[]>([])),
                finalize(() => this.loadingCatalog.set(false)),
            )),
        ),
        { initialValue: [] as Product[] },
    );

    readonly catalog = computed(() => this.products().map(toCatalogProduct));

    readonly units = toSignal(
        this.unitApi.getAll().pipe(catchError(() => of<IUnit[]>([]))),
        { initialValue: [] as IUnit[] },
    );

    readonly taxRates = toSignal(
        this.taxRateApi.getAll().pipe(catchError(() => of<ITaxRate[]>([]))),
        { initialValue: [] as ITaxRate[] },
    );

    // Billing intervals for subscriptions. Excludes NextInvoice (one-time),
    // mirroring the legacy GetProductSubscriptionDialog (`po.id < 100`).
    readonly payableOptions = toSignal(
        this.payableOptionApi.getAll().pipe(
            map(list => list.filter(po => po.id < PayableOptionId.NextInvoice)),
            catchError(() => of<PayableOption[]>([])),
        ),
        { initialValue: [] as PayableOption[] },
    );

    // Real product categories (FK) for the new-product form — needed so an ad-hoc
    // line saved to the catalog carries a proper category id (mirrors the legacy
    // product form, which only lets you pick existing categories).
    readonly categoryOptions = toSignal(
        this.categoryApi.getAll().pipe(catchError(() => of<ICategory[]>([]))),
        { initialValue: [] as ICategory[] },
    );

    // ── Read-only history (real data, scoped to the selected existing
    // person/company) ──
    readonly isExistingPerson = computed(() => this.facade.selectedPerson()?.origin === 'existing');

    // Re-fires when the selected existing person/company changes. `null` (new /
    // quick-created person, or none selected) → nothing to load.
    private readonly historyKey = computed(() => {
        const p = this.facade.selectedPerson();
        return p && p.origin === 'existing' ? { id: p.id, kind: p.kind } : null;
    });

    // Single source for both sub-tabs so the person/company GET + financial-records
    // GET fire once per selection (the public signals below are derived, not
    // separate subscriptions). The loading flag is carried through the stream via
    // `startWith` rather than written imperatively — writing a signal inside the
    // `toObservable` effect would trip NG0600.
    private readonly history = toSignal(
        toObservable(this.historyKey).pipe(
            switchMap(key => {
                if (!key) {
                    return of({ loading: false, subscriptions: [] as BookingV2Subscription[], receipts: [] as BookingV2Receipt[] });
                }
                const entity$ = key.kind === 'company'
                    ? this.companyApi.getOne(key.id)
                    : this.personApi.getOne(key.id);
                const req = new PageRequest<BcmFinancialRecord>('date', 'desc');
                req.props.set(key.kind === 'company' ? 'bcm_companies_id' : 'bcm_persons_id', String(key.id));
                return combineLatest([entity$, this.financialRecordsApi.getAll(req)]).pipe(
                    map(([entity, page]) => ({
                        loading: false,
                        subscriptions: ((entity as any)?.subscriptions ?? []).map(toBookingSubscription),
                        receipts: (page?.results ?? []).map(toBookingReceipt),
                    })),
                    catchError(() => of({ loading: false, subscriptions: [] as BookingV2Subscription[], receipts: [] as BookingV2Receipt[] })),
                    startWith({ loading: true, subscriptions: [] as BookingV2Subscription[], receipts: [] as BookingV2Receipt[] }),
                );
            }),
        ),
        { initialValue: { loading: false, subscriptions: [] as BookingV2Subscription[], receipts: [] as BookingV2Receipt[] } },
    );

    readonly loadingHistory = computed(() => this.history().loading);
    readonly subscriptions = computed(() => this.history().subscriptions);
    readonly receipts = computed(() => this.history().receipts);

    readonly activeSubscriptions = computed(() => this.subscriptions().filter(s => s.isActive));

    readonly subTab = signal<ServicesSubTab>('services');

    readonly subTabs = computed<U2bTabSwitcherItem<ServicesSubTab>[]>(() => [
        { id: 'services', labelKey: BOOKING_V2_ADVANCED_SERVICES_TAB_LABEL_KEY, icon: 'shopping_bag', count: this.services().length },
        { id: 'subscriptions', labelKey: 'bookingV2.advanced.services.subtabs.subscriptions', icon: 'autorenew', count: this.activeSubscriptions().length },
        { id: 'receipts', labelKey: 'bookingV2.advanced.services.subtabs.receipts', icon: 'receipt_long', count: this.receipts().length },
    ]);

    // ── New ad-hoc position form ──
    readonly showNewProductForm = signal<boolean>(false);
    readonly npName = signal<string>('');
    readonly npCategory = signal<ICategory | null>(null);
    readonly npUnitId = signal<number | null>(null);
    readonly npPrice = signal<number | null>(null);
    readonly npTaxRateId = signal<number | null>(null);
    // "auch im Katalog speichern": also persist the ad-hoc line as a reusable
    // catalog product (#6286). When set, a category becomes mandatory.
    readonly npSaveToCatalog = signal<boolean>(false);
    readonly savingToCatalog = signal<boolean>(false);

    readonly npDuplicate = computed(() => {
        const name = this.npName().trim().toLowerCase();
        if (!name) {
            return null;
        }
        return this.catalog().find(p => p.name.toLowerCase() === name) ?? null;
    });

    // Category is only required when the line is saved to the catalog.
    readonly npCategoryMissing = computed(() => this.npSaveToCatalog() && this.npCategory() === null);

    readonly npValid = computed(() =>
        this.npName().trim().length > 0
        && this.npPrice() !== null
        && this.npUnitId() !== null
        && this.npTaxRateId() !== null
        && !this.npCategoryMissing(),
    );

    // ── Inline detail editor ──
    readonly editingId = signal<string | null>(null);
    readonly editName = signal<string>('');
    // The name when the editor opened — a re-evaluation may rename the line meanwhile, and an
    // untouched field must not write the old name back as the user's own.
    private editNameAtOpen = '';
    readonly editPricingRelationId = signal<number | null>(null);
    readonly editCostCenter = signal<number | null>(null);
    readonly editQuantity = signal<number>(1);
    readonly editUnitId = signal<number | null>(null);
    readonly editPrice = signal<number>(0);
    readonly editTaxRateId = signal<number | null>(null);
    readonly editDiscount = signal<number | null>(null);
    readonly editServiceStart = signal<Date | null>(null);
    readonly editServiceEnd = signal<Date | null>(null);
    readonly editServiceEndOpen = signal<boolean>(false);

    // ── Subscription (mirrors the legacy GetProductSubscriptionDialog) ──
    readonly showSubscriptionForm = signal<boolean>(false);
    readonly subPayableOptionId = signal<number | null>(null);
    readonly subFromDate = signal<Date | null>(null);       // Erste Position
    readonly subToDate = signal<Date | null>(null);         // Letzte Position
    readonly subToOpen = signal<boolean>(true);
    readonly subVestingFrom = signal<Date | null>(null);    // Leistungszeitraum von
    readonly subVestingUntil = signal<Date | null>(null);   // Leistungszeitraum bis
    readonly subLastDayOfMonth = signal<boolean>(false);

    // lastDayOfMonth only applies to month-aligned intervals (matches legacy).
    readonly lastDayOfMonthApplicable = computed(() => {
        const id = this.subPayableOptionId();
        return id === PayableOptionId.Monthly
            || id === PayableOptionId.HalfYearly
            || id === PayableOptionId.QuarterYearly;
    });

    // Hourly billing needs the time-of-day on the begin/end dates, not just the day.
    readonly isHourlySubscription = computed(() => this.subPayableOptionId() === PayableOptionId.Hourly);

    // The placeholder has to follow the picker between date-only and date+time.
    readonly subscriptionDatePlaceholderKey = computed(() => this.isHourlySubscription()
        ? 'bookingV2.dateRange.datetimePlaceholder'
        : 'bookingV2.advanced.power.datePlaceholder');

    readonly editVatValue = computed(() =>
        this.taxRates().find(t => t.id === this.editTaxRateId())?.value ?? 0,
    );

    readonly editGross = computed(() =>
        grossOf(this.editQuantity(), this.editPrice(), this.editVatValue(), this.editDiscount() ?? undefined),
    );

    readonly editingService = computed(() =>
        this.services().find(s => s.id === this.editingId()) ?? null,
    );

    readonly editingServiceName = computed(() => this.editingService()?.name ?? '');

    // A rule-based line is priced by BookingV2ServicePricingService → the price field
    // is read-only (it would be overwritten by the next re-evaluation anyway) and the
    // matched rule name is shown instead.
    readonly editingIsDynamic = computed(() => !!this.editingService()?.hasDynamicPrice);

    // "Übernehme die Menge von:" chips for an m²-billed Leistung — the same convenience the
    // Abo/Bierdeckel dialogs offer, so the harbour does not have to look the boat's area up by
    // hand. Only rendered while the edited line is in m². (#6944)
    readonly areaQuantityButtons = computed<ProduktQuantityButton[]>(() => {
        const unit = this.units().find(u => u.id === this.editUnitId());

        if (unit?.uniqueName !== UnitUniqueName.SQM) {
            return [];
        }

        const boat = this.facade.selectedBoat();

        return buildAreaQuantityButtons({
            // Only `sqm`, never the option's length/width: `length` here is the LüA, so letting the
            // helper fall back to length × width would offer an area the billing engine (which
            // resolves from the hull length) cannot reproduce. No area -> no chip. (#6944)
            boats: [boat && {sqm: boat.sqm, name: boat.name}],
            berths: this.facade.selectedBerths(),
        });
    });

    readonly editingRuleName = computed(() => this.editingService()?.dynamicRuleName ?? null);

    // The edited line's rule names Beziehungen the booking's own does not satisfy →
    // the editor asks which one to price with (as on the Bierdeckel). (#7044)
    readonly editingPricingRelationChoices = computed<BookingV2RelationOption[] | null>(() => {
        const service = this.editingService();
        return service && this.relationPricing.needsPricingRelation(service)
            ? this.relationPricing.pricingRelationOptions(service.tenantRelationCriteriaIds)
            : null;
    });

    // Row hint: the rule needs a Beziehung that has not been picked yet.
    pricingRelationMissing(service: BookingV2Service): boolean {
        return this.relationPricing.needsPricingRelation(service) && service.pricingRelationId == null;
    }

    // Service-context season picker + warnings (the relation context has its own in
    // the person step). Both bind the same global selectedSeason via the shared
    // pricing service; seasonMissing (combined) blocks Save.
    readonly serviceShowSeasonPicker = this.relationPricing.serviceShowSeasonPicker;
    readonly servicePriceWarnings = this.relationPricing.servicePriceWarnings;
    readonly seasons = this.relationPricing.seasons;
    readonly selectedSeason = this.facade.selectedSeason;
    readonly seasonMissing = this.relationPricing.seasonMissing;

    // Service-context Lagerplatz picker (same dedup vs. the relation card). Binds the
    // global selectedWinterStorage; not a Save-gate, just `*` + hint while unset.
    readonly serviceShowWinterStoragePicker = this.relationPricing.serviceShowWinterStoragePicker;
    readonly winterStorages = this.relationPricing.winterStorages;
    readonly selectedWinterStorage = this.facade.selectedWinterStorage;
    readonly winterStorageMissing = this.relationPricing.winterStorageMissing;

    setSeason(season: Season | null): void {
        this.relationPricing.setSeason(season);
    }

    setWinterStorage(option: BookingV2WinterStorageOption | null): void {
        this.relationPricing.setWinterStorage(option);
    }

    // Subscriptions need a catalog product (ad-hoc free-text lines have no
    // product to bind a ProductSubscription to).
    readonly canSubscribe = computed(() => this.editingService()?.productId != null);

    // A subscription must have a billing interval (payableOption); otherwise the
    // line would be silently dropped at commit (mapSubscriptions filters it out).
    // Mirrors applyEdit()'s `isSubscription` condition + the null-interval test,
    // so it gates exactly the rows that would be lost.
    readonly subscriptionIntervalMissing = computed(() =>
        this.canSubscribe() && this.showSubscriptionForm() && this.subPayableOptionId() === null,
    );

    readonly totalGross = computed(() =>
        this.services().reduce((sum, s) => sum + grossOf(s.quantity, s.price, s.vatValue, s.discount), 0),
    );

    readonly subscriptionsTotal = computed(() =>
        this.activeSubscriptions().reduce((s, a) => s + a.total, 0),
    );

    // ── Bulk selection (positions) ──
    readonly selectedIds = signal<ReadonlySet<string>>(new Set<string>());
    readonly selectedCount = computed(() => this.selectedIds().size);
    readonly allSelected = computed(() => {
        const list = this.services();
        return list.length > 0 && list.every(s => this.selectedIds().has(s.id));
    });
    readonly someSelected = computed(() => this.selectedCount() > 0 && !this.allSelected());

    // Shared, content-independent column tracks for the header, body rows and
    // footer. Only the description column flexes; fixed tracks elsewhere keep all
    // three grids aligned even when a row carries a long price. The leading
    // checkbox + trailing actions columns exist only in editable mode.
    // Tracks: [checkbox] description quantity unit unitPrice total [actions]
    readonly gridTemplate = computed(() =>
        this.readOnly()
            ? 'minmax(4.5rem, 5.25rem) minmax(10rem, 1fr) minmax(5.75rem, 6.5rem) minmax(7rem, 8.25rem) minmax(7.25rem, 8.5rem) minmax(7.5rem, 9rem) 4.5rem'
            : '2.5rem minmax(4.5rem, 5.25rem) minmax(10rem, 1fr) minmax(5.75rem, 6.5rem) minmax(7rem, 8.25rem) minmax(7.25rem, 8.5rem) minmax(7.5rem, 9rem) 4.5rem',
    );

    private bulkDeleteOverlay: OverlayRef | null = null;

    // Drop ids whose row no longer exists (single delete, bulk delete, or an
    // edit-mode reload that replaces the list) so the bulk bar count stays honest.
    private readonly pruneSelection = effect(() => {
        const present = new Set(this.services().map(s => s.id));
        untracked(() => {
            const current = this.selectedIds();
            if ([...current].some(id => !present.has(id))) {
                this.selectedIds.set(new Set([...current].filter(id => present.has(id))));
            }
        });
    });

    setSubTab(tab: ServicesSubTab): void {
        this.subTab.set(tab);
    }

    toggleNewProductForm(): void {
        const next = !this.showNewProductForm();
        this.showNewProductForm.set(next);
        if (next) {
            this.npName.set('');
            this.npCategory.set(null);
            this.npUnitId.set(this.defaultUnitId());
            this.npPrice.set(null);
            this.npTaxRateId.set(this.defaultTaxRateId());
            this.npSaveToCatalog.set(false);
        }
    }

    closeNewProductForm(): void {
        this.showNewProductForm.set(false);
        this.npName.set('');
        this.npCategory.set(null);
        this.npUnitId.set(null);
        this.npPrice.set(null);
        this.npTaxRateId.set(null);
        this.npSaveToCatalog.set(false);
    }

    onInlineEntryCommitted(payload: BookingV2ServiceInlineCommit): void {
        const catalogProduct = payload.product;
        if (catalogProduct) {
            const backendProduct = this.products().find(p => p.id === catalogProduct.id) ?? catalogProduct;
            const service = createBookingV2ServiceFromProduct(backendProduct, this.facade.dateRange(), {
                quantity: payload.quantity,
                unitId: payload.unitId,
                unitLabelKey: this.unitLabelKeyOf(payload.unitId),
                price: payload.price,
                taxRateId: payload.taxRateId,
                vatValue: payload.vatValue,
            });
            this.facade.services.update(list => [...list, service]);
            return;
        }

        this.appendAdHocServiceLine({
            name: payload.title,
            quantity: payload.quantity,
            unitId: payload.unitId,
            price: payload.price,
            taxRateId: payload.taxRateId,
        });
    }

    openInlineAdvancedEntry(): void {
        this.toggleNewProductForm();
    }

    itemNumberFor(service: BookingV2Service): string {
        if (!service.productId) {
            return '';
        }

        return this.products().find(p => p.id === service.productId)?.itemNumber ?? '';
    }

    canEditInline(service: BookingV2Service): boolean {
        return !this.readOnly() && !service.isProtected;
    }

    readonly inlinePriceEditingId = signal<string | null>(null);

    isInlinePriceEditing(serviceId: string): boolean {
        return this.inlinePriceEditingId() === serviceId;
    }

    openInlinePriceEdit(serviceId: string): void {
        if (this.inlinePriceEditingId() === serviceId) {
            return;
        }
        this.inlinePriceEditingId.set(serviceId);
        queueMicrotask(() => {
            const el = this.hostRef.nativeElement.querySelector(
                `[data-booking-v2-inline-price="${serviceId}"]`,
            ) as HTMLInputElement | null;
            el?.focus();
            el?.select();
        });
    }

    closeInlinePriceEdit(): void {
        this.inlinePriceEditingId.set(null);
    }

    onInlinePriceKeydown(event: Event): void {
        if (!(event instanceof KeyboardEvent) || event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        this.closeInlinePriceEdit();
    }

    patchServiceInline(
        id: string,
        patch: Partial<Pick<BookingV2Service, 'quantity' | 'unitId' | 'price'>>,
    ): void {
        this.facade.services.update(list =>
            list.map(s => {
                if (s.id !== id) {
                    return s;
                }

                const unitId = patch.unitId ?? s.unitId;

                return {
                    ...s,
                    ...patch,
                    unitLabelKey: patch.unitId !== undefined ? this.unitLabelKeyOf(unitId) : s.unitLabelKey,
                    price: s.hasDynamicPrice ? s.price : (patch.price ?? s.price),
                };
            }),
        );
    }

    addAdHocPosition(): void {
        if (!this.npValid() || this.savingToCatalog()) {
            return;
        }
        if (this.npSaveToCatalog()) {
            this.saveToCatalogAndAdd();
        } else {
            this.addServiceLine(null);
        }
    }

    // Append the new ad-hoc line to the booking. `productId` is the freshly created
    // catalog product (when saved) or null for a one-off line.
    readonly inlineFallbackTaxRateId = computed(() => this.defaultTaxRateId());
    readonly inlineFallbackUnitId = computed(() => this.defaultUnitId());

    private addServiceLine(productId: number | null): void {
        this.appendAdHocServiceLine({
            name: this.npName().trim(),
            quantity: 1,
            unitId: this.npUnitId(),
            price: this.npPrice() ?? 0,
            taxRateId: this.npTaxRateId(),
            productId,
        });
        this.closeNewProductForm();
    }

    private appendAdHocServiceLine(opts: {
        name: string;
        quantity: number;
        unitId: number | null;
        price: number;
        taxRateId: number | null;
        productId?: number | null;
        category?: string | null;
    }): void {
        const { from, to, openEnd } = this.facade.dateRange();
        const unitId = opts.unitId;
        const taxRateId = opts.taxRateId;
        const service: BookingV2Service = {
            id: uuidv4(),
            productId: opts.productId ?? null,
            name: opts.name.trim(),
            category: opts.category ?? null,
            quantity: opts.quantity,
            unitId,
            unitLabelKey: this.unitLabelKeyOf(unitId),
            price: opts.price,
            taxRateId,
            vatValue: this.vatValueOf(taxRateId),
            serviceStart: from,
            serviceEnd: to,
            serviceEndOpen: openEnd,
        };
        this.facade.services.update(list => [...list, service]);
    }

    // Persist the ad-hoc service as a catalog product first, then add the booking
    // line referencing the new productId. Payload shape matches productDataSanitizer
    // (name/price/unit.id/taxRate.id/category.id). On failure the form stays open.
    private saveToCatalogAndAdd(): void {
        const category = this.npCategory();
        const unitId = this.npUnitId();
        const taxRateId = this.npTaxRateId();
        if (!category || unitId === null || taxRateId === null) {
            return;
        }
        const payload = {
            name: this.npName().trim(),
            price: this.npPrice() ?? 0,
            unit: { id: unitId },
            taxRate: { id: taxRateId },
            category: { id: category.id },
            hasDynamicPrice: false,
            tags: [], // BE iteriert req.body.tags bedingungslos (processTags)
        };
        this.savingToCatalog.set(true);
        this.productsApi.add(payload).pipe(
            finalize(() => this.savingToCatalog.set(false)),
        ).subscribe({
            next: (productId) => {
                this.notifications.showSuccess(
                    TolgeeService.translate('bookingV2.advanced.services.newProduct.savedToCatalog'),
                );
                this.catalogReload.update(n => n + 1);
                this.addServiceLine(productId);
            },
            error: () => this.notifications.showError(
                TolgeeService.translate('bookingV2.advanced.services.newProduct.saveFailed'),
            ),
        });
    }

    openDetailEditor(service: BookingV2Service): void {
        if (this.editingId() === service.id) {
            return;
        }

        this.openEditor(service);
    }

    openEditor(service: BookingV2Service): void {
        this.editingId.set(service.id);
        this.editName.set(service.name);
        this.editNameAtOpen = service.name;
        this.editPricingRelationId.set(service.pricingRelationId ?? null);
        this.editCostCenter.set(service.costCenterId ?? null);
        this.editQuantity.set(service.quantity);
        this.editUnitId.set(service.unitId);
        this.editPrice.set(service.price);
        this.editTaxRateId.set(service.taxRateId);
        this.editDiscount.set(service.discount ?? null);
        this.editServiceStart.set(service.serviceStart);
        this.editServiceEnd.set(service.serviceEnd);
        this.editServiceEndOpen.set(service.serviceEndOpen ?? false);
        this.showSubscriptionForm.set(service.isSubscription ?? false);
        const cfg = service.subscriptionConfig;
        const from = this.facade.dateRange().from;
        this.subPayableOptionId.set(cfg?.payableOptionId ?? null);
        this.subFromDate.set(cfg?.fromDate ?? from);
        this.subToDate.set(cfg?.toDate ?? null);
        this.subToOpen.set(cfg ? cfg.toDate === null : true);
        this.subVestingFrom.set(cfg?.vestingPeriodFrom ?? from);
        this.subVestingUntil.set(cfg?.vestingPeriodUntil ?? null);
        this.subLastDayOfMonth.set(cfg?.lastDayOfMonth ?? false);
    }

    closeEditor(): void {
        this.editingId.set(null);
        this.showSubscriptionForm.set(false);
    }

    onServiceEndOpenChange(checked: boolean): void {
        this.editServiceEndOpen.set(checked);
        if (checked) {
            this.editServiceEnd.set(null);
        }
    }

    onSubToOpenChange(checked: boolean): void {
        this.subToOpen.set(checked);
        if (checked) {
            this.subToDate.set(null);
        }
    }

    onSubPayableOptionChange(id: number | null): void {
        this.subPayableOptionId.set(id);
        // Keep lastDayOfMonth meaningful only for month-aligned intervals.
        if (!this.lastDayOfMonthApplicable()) {
            this.subLastDayOfMonth.set(false);
        }
    }

    // Auto-fill the vesting end from the interval (mirrors the legacy dialog):
    // until = (from + 1 interval) − 1 minute; for month-aligned intervals
    // starting on the 1st, also enable lastDayOfMonth.
    onSubVestingFromChange(date: Date | null): void {
        this.subVestingFrom.set(date);
        if (!date || this.subPayableOptionId() === null || this.subVestingUntil() !== null) {
            return;
        }
        const until = this.addInterval(date, this.subPayableOptionId());
        if (!until) {
            return;
        }
        this.subVestingUntil.set(subMinutes(until, 1));
        if (isFirstDayOfMonth(date) && this.lastDayOfMonthApplicable()) {
            this.onSubLastDayOfMonthChange(true);
        }
    }

    onSubLastDayOfMonthChange(checked: boolean): void {
        this.subLastDayOfMonth.set(checked);
        if (checked) {
            const base = this.subVestingUntil() ?? this.subVestingFrom();
            if (base) {
                this.subVestingUntil.set(endOfMonth(base));
            }
        }
    }

    private addInterval(date: Date, payableOptionId: number | null): Date | null {
        switch (payableOptionId) {
            case PayableOptionId.Yearly: return addYears(date, 1);
            case PayableOptionId.HalfYearly: return addMonths(date, 6);
            case PayableOptionId.QuarterYearly: return addMonths(date, 3);
            case PayableOptionId.Monthly: return addMonths(date, 1);
            case PayableOptionId.Weekly: return addWeeks(date, 1);
            case PayableOptionId.Daily: return addDays(date, 1);
            case PayableOptionId.Hourly: return addHours(date, 1);
            default: return null;
        }
    }

    applyEdit(): void {
        const id = this.editingId();
        if (id === null) {
            return;
        }
        const isSubscription = this.canSubscribe() && this.showSubscriptionForm();
        const unitId = this.editUnitId();
        const taxRateId = this.editTaxRateId();
        const payableOptionId = this.subPayableOptionId();
        const config: BookingV2SubscriptionConfig | undefined = isSubscription
            ? {
                payableOptionId,
                payableOptionLabelKey: this.payableOptions().find(po => po.id === payableOptionId)?.translationKey ?? null,
                fromDate: this.subFromDate(),
                toDate: this.subToOpen() ? null : this.subToDate(),
                vestingPeriodFrom: this.subVestingFrom(),
                vestingPeriodUntil: this.subVestingUntil(),
                lastDayOfMonth: this.subLastDayOfMonth(),
            }
            : undefined;
        const typedName = this.editName().trim();
        const nameTouched = typedName !== this.editNameAtOpen.trim();
        this.facade.services.update(list =>
            list.map(s => (s.id !== id ? s : {
                ...s,
                ...(nameTouched ? editedServiceName(s, typedName) : {}),
                pricingRelationId: this.editingPricingRelationChoices() ? this.editPricingRelationId() : (s.pricingRelationId ?? null),
                costCenterId: this.editCostCenter(),
                quantity: this.editQuantity(),
                unitId,
                unitLabelKey: this.unitLabelKeyOf(unitId) ?? s.unitLabelKey,
                // A dynamic line's price is service-owned (read-only field) — keep it;
                // dynamicPrice/dynamicRuleName ride along via `...s`.
                price: s.hasDynamicPrice ? s.price : this.editPrice(),
                taxRateId,
                vatValue: this.vatValueOf(taxRateId),
                discount: this.editDiscount() ?? undefined,
                serviceStart: isSubscription ? s.serviceStart : this.editServiceStart(),
                serviceEnd: isSubscription ? s.serviceEnd : this.editServiceEnd(),
                serviceEndOpen: isSubscription ? s.serviceEndOpen : this.editServiceEndOpen(),
                isSubscription,
                subscriptionConfig: config,
            })),
        );
        this.closeEditor();
    }

    removeService(id: string): void {
        this.facade.services.update(list => list.filter(s => s.id !== id));
        if (this.editingId() === id) {
            this.closeEditor();
        }
    }

    isEditing(id: string): boolean {
        return this.editingId() === id;
    }

    fmt = formatMoney;

    fmtQty = formatQuantity;

    isSelected(id: string): boolean {
        return this.selectedIds().has(id);
    }

    toggleSelected(id: string): void {
        const next = new Set(this.selectedIds());
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        this.selectedIds.set(next);
    }

    toggleSelectAll(): void {
        this.selectedIds.set(
            this.allSelected() ? new Set<string>() : new Set(this.services().map(s => s.id)),
        );
    }

    requestBulkDelete(): void {
        if (this.selectedCount() === 0 || this.bulkDeleteOverlay) {
            return;
        }
        const overlayRef = this.overlay.create({
            hasBackdrop: false,
            scrollStrategy: this.overlay.scrollStrategies.block(),
            positionStrategy: this.overlay.position().global(),
        });
        const compRef = overlayRef.attach(new ComponentPortal(U2bConfirmDialogComponent));
        compRef.setInput('severity', 'danger');
        compRef.setInput('icon', 'delete');
        compRef.setInput('titleKey', 'bookingV2.advanced.services.bulk.confirmTitle');
        compRef.setInput('messageKey', 'bookingV2.advanced.services.bulk.confirmBody');
        compRef.setInput('messageParams', { count: this.selectedCount() });
        compRef.setInput('confirmKey', 'common.action.delete');
        compRef.setInput('cancelKey', 'common.cancel');
        compRef.instance.confirmed.subscribe(() => {
            this.applyBulkDelete();
            this.disposeBulkDeleteOverlay();
        });
        compRef.instance.cancelled.subscribe(() => this.disposeBulkDeleteOverlay());
        this.bulkDeleteOverlay = overlayRef;
    }

    private disposeBulkDeleteOverlay(): void {
        this.bulkDeleteOverlay?.dispose();
        this.bulkDeleteOverlay = null;
    }

    private applyBulkDelete(): void {
        const ids = this.selectedIds();
        const editingId = this.editingId();
        this.facade.services.update(list => list.filter(s => !ids.has(s.id)));
        if (editingId !== null && ids.has(editingId)) {
            this.closeEditor();
        }
        this.selectedIds.set(new Set<string>());
    }

    private defaultUnitId(): number | null {
        const units = this.units();
        return (units.find(u => u.uniqueName === UnitUniqueName.PIECE) ?? units[0])?.id ?? null;
    }

    private defaultTaxRateId(): number | null {
        const rates = this.taxRates();
        return (rates.find(t => t.value === 19) ?? rates[0])?.id ?? null;
    }

    private unitLabelKeyOf(unitId: number | null): string | null {
        return this.units().find(u => u.id === unitId)?.translationKey ?? null;
    }

    private vatValueOf(taxRateId: number | null): number {
        return this.taxRates().find(t => t.id === taxRateId)?.value ?? 0;
    }
}
